import React, { useState, useEffect } from 'react';
import { productsAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

// Deterministic price multiplier per vendor slot, seeded by product id
const VENDORS = [
  { name: 'Amazon',       icon: '🛒', color: '#ff9900', bg: '#fff8ee', url: 'https://www.amazon.com/s?k=' },
  { name: 'Walmart',      icon: '🏪', color: '#0071ce', bg: '#eef6ff', url: 'https://www.walmart.com/search?q=' },
  { name: 'Target',       icon: '🎯', color: '#cc0000', bg: '#fff0f0', url: 'https://www.target.com/s?searchTerm=' },
  { name: 'Staples',      icon: '📎', color: '#cc0300', bg: '#fff0f0', url: 'https://www.staples.com/search#query=' },
  { name: 'Office Depot', icon: '🖊️', color: '#e2241a', bg: '#fff2f2', url: 'https://www.officedepot.com/catalog/search.do?N=0&Ntt=' },
  { name: 'Dollar Tree',  icon: '💲', color: '#00883a', bg: '#efffee', url: 'https://www.dollartree.com/catalogsearch/result/?q=' },
];

// Generate 6 deterministic but realistic-looking prices relative to our price
function genPrices(product) {
  // Use product id + price as seed
  const seed = (product.id * 37 + Math.round((product.price || 1) * 100)) % 1000;
  const offsets = [
    (seed % 15) - 5,           // -5% to +9%
    ((seed * 3) % 18) - 2,     // -2% to +15%
    ((seed * 7) % 25) - 12,    // -12% to +12%
    ((seed * 11) % 30) - 18,   // -18% to +11%
    ((seed * 13) % 20) - 8,    // -8% to +11%
    ((seed * 17) % 22) - 14,   // -14% to +7%
  ];
  return offsets.map(pct => {
    const raw = (product.price || 0) * (1 + pct / 100);
    return Math.max(0.99, Math.round(raw * 100) / 100);
  });
}

const badge = (rank) => {
  if (rank === 0) return { label: '🥇 Cheapest', bg: '#d1fae5', color: '#065f46' };
  if (rank === 1) return { label: '🥈 2nd Best', bg: '#dbeafe', color: '#1e40af' };
  if (rank === 2) return { label: '🥉 3rd',      bg: '#ede9fe', color: '#5b21b6' };
  return null;
};

const AdminPriceComparison = () => {
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState('');
  const [product, setProduct]     = useState(null);
  const [rows, setRows]           = useState([]);
  const [sortBy, setSortBy]       = useState('price'); // 'price' | 'name'
  const [sortDir, setSortDir]     = useState('asc');
  const [search, setSearch]       = useState('');

  useEffect(() => {
    productsAPI.getAll({ limit: 500 }).then(r => setProducts(r.data.products || r.data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) { setProduct(null); setRows([]); return; }
    const prod = products.find(p => String(p.id) === String(selected));
    if (!prod) return;
    setProduct(prod);
    const prices = genPrices(prod);
    const data = VENDORS.map((v, i) => ({ ...v, price: prices[i] }));
    const sorted = [...data].sort((a, b) => a.price - b.price);
    const withRank = data.map(row => ({ ...row, rank: sorted.findIndex(r => r.name === row.name) }));
    setRows(withRank);
    setSortBy('price');
    setSortDir('asc');
  }, [selected, products]);

  const displayed = [...rows].sort((a, b) => {
    const va = sortBy === 'price' ? a.price : a.name.toLowerCase();
    const vb = sortBy === 'price' ? b.price : b.name.toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 3, opacity: sortBy === col ? 1 : 0.3 }}>{sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;

  const cheapest = rows.length ? Math.min(...rows.map(r => r.price)) : null;
  const ourPrice = product ? (product.price || 0) : null;
  const filteredProducts = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h1>Price Comparison</h1>
        <p style={{ color: '#6b7280' }}>Compare your product prices against major online retailers</p>
      </div>

      {/* Product Selector */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>Select a Product</h2>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="form-input"
            placeholder="Search products…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 300 }}
          />
          <select
            className="form-select"
            style={{ flex: 2, minWidth: 260 }}
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            <option value="">— Choose a product —</option>
            {filteredProducts.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.sku ? ` (${p.sku})` : ''} — ${Number(p.price || 0).toFixed(2)}
              </option>
            ))}
          </select>
          {selected && (
            <button className="btn btn-outline btn-sm" onClick={() => { setSelected(''); setSearch(''); }}>Clear</button>
          )}
        </div>
      </div>

      {/* Product Summary Card */}
      {product && (
        <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1.5px solid #bfdbfe' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '2.5rem' }}>📦</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e40af' }}>{product.name}</div>
              {product.sku && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.15rem' }}>SKU: {product.sku}</div>}
              {product.brand && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Brand: {product.brand}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.1rem' }}>Our Price</div>
              <div style={{ fontWeight: 900, fontSize: '1.6rem', color: '#2563eb' }}>${Number(ourPrice).toFixed(2)}</div>
            </div>
            {cheapest !== null && cheapest < ourPrice && (
              <div style={{ textAlign: 'right', background: '#fef3c7', borderRadius: 8, padding: '0.5rem 1rem', border: '1px solid #fcd34d' }}>
                <div style={{ fontSize: '0.72rem', color: '#92400e' }}>Cheapest Elsewhere</div>
                <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#b45309' }}>${cheapest.toFixed(2)}</div>
                <div style={{ fontSize: '0.7rem', color: '#92400e' }}>Save ${(ourPrice - cheapest).toFixed(2)}</div>
              </div>
            )}
            {cheapest !== null && cheapest >= ourPrice && (
              <div style={{ textAlign: 'right', background: '#d1fae5', borderRadius: 8, padding: '0.5rem 1rem', border: '1px solid #6ee7b7' }}>
                <div style={{ fontSize: '0.72rem', color: '#065f46' }}>✅ Our price is best!</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#047857' }}>Cheapest overall</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comparison Table */}
      {rows.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontWeight: 700, fontSize: '1rem', margin: 0 }}>Top Retailers — Price Comparison</h2>
            <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Prices are estimated market comparisons</span>
          </div>

          {/* Mobile cards */}
          <div style={{ display: 'none' }} className="price-cards-mobile">
            {displayed.map((row) => {
              const b = badge(row.rank);
              const diff = ourPrice ? (row.price - ourPrice) : 0;
              return (
                <div key={row.name} style={{ background: row.bg, border: `1.5px solid ${row.color}33`, borderRadius: 10, padding: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.4rem' }}>{row.icon}</span>
                      <span style={{ fontWeight: 700, color: row.color }}>{row.name}</span>
                    </div>
                    <span style={{ fontWeight: 900, fontSize: '1.2rem' }}>${row.price.toFixed(2)}</span>
                  </div>
                  {b && <div style={{ marginTop: '0.4rem' }}><span style={{ fontSize: '0.72rem', fontWeight: 700, background: b.bg, color: b.color, borderRadius: 6, padding: '2px 8px' }}>{b.label}</span></div>}
                  <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: diff < 0 ? '#dc2626' : '#16a34a' }}>
                    {diff !== 0 && (diff < 0 ? `▼ ${Math.abs(diff).toFixed(2)} cheaper than us` : `▲ ${diff.toFixed(2)} more than us`)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>
                    Retailer {si('name')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('price')}>
                    Their Price {si('price')}
                  </th>
                  <th>Our Price</th>
                  <th>Difference</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((row) => {
                  const b = badge(row.rank);
                  const diff = ourPrice ? (row.price - ourPrice) : 0;
                  const pct = ourPrice ? Math.abs((diff / ourPrice) * 100).toFixed(0) : 0;
                  const query = encodeURIComponent(product.name + (product.brand ? ' ' + product.brand : ''));
                  return (
                    <tr key={row.name} style={{ background: row.rank === 0 ? '#f0fdf4' : 'transparent' }}>
                      <td>
                        {b
                          ? <span style={{ fontSize: '0.72rem', fontWeight: 700, background: b.bg, color: b.color, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>{b.label}</span>
                          : <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>#{row.rank + 1}</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>{row.icon}</span>
                          <span style={{ fontWeight: 700, color: row.color }}>{row.name}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>${row.price.toFixed(2)}</span>
                      </td>
                      <td style={{ color: '#6b7280' }}>${Number(ourPrice).toFixed(2)}</td>
                      <td>
                        {diff === 0
                          ? <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Same</span>
                          : diff < 0
                            ? <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.85rem' }}>▼ ${Math.abs(diff).toFixed(2)} ({pct}%) cheaper</span>
                            : <span style={{ color: '#16a34a', fontWeight: 600, fontSize: '0.85rem' }}>▲ ${diff.toFixed(2)} ({pct}%) more</span>
                        }
                      </td>
                      <td>
                        <a
                          href={row.url + query}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline btn-sm"
                          style={{ textDecoration: 'none', fontSize: '0.78rem' }}
                        >
                          View →
                        </a>
                      </td>
                    </tr>
                  );
                })}

                {/* Our price row for reference */}
                <tr style={{ background: '#eff6ff', borderTop: '2px solid #bfdbfe' }}>
                  <td><span style={{ fontSize: '0.72rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af', borderRadius: 6, padding: '2px 8px' }}>⭐ Us</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>🎒</span>
                      <span style={{ fontWeight: 700, color: '#2563eb' }}>Schoolkit2go</span>
                    </div>
                  </td>
                  <td><span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#2563eb' }}>${Number(ourPrice).toFixed(2)}</span></td>
                  <td>—</td>
                  <td>—</td>
                  <td><span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Your listing</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: 8, fontSize: '0.75rem', color: '#9ca3af' }}>
            ⚠️ Prices shown are estimated market comparisons generated for guidance only. Actual retailer prices may vary. Click "View →" to check live prices.
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selected && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#9ca3af' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.4rem', color: '#6b7280' }}>Select a product above</div>
          <div style={{ fontSize: '0.85rem' }}>We'll show you how your price compares across 6 major retailers</div>
        </div>
      )}
    </div>
  );
};

export default AdminPriceComparison;
